import type { ReactElement } from "react"
import { twMerge } from "tailwind-merge"
import {
  Image,
  type ImageProps,
} from "@/components/resources/components/ui/Image"
import type { Teaser } from "@/components/resources/data/posts.types"
import { ProjectLogo } from "./ProjectLogo"

type Size = "xxl" | "xl" | "l" | "m" | "s"

type BlogImageProps = Pick<
  Teaser,
  "category" | "releaseNumber" | "projectMeta"
> &
  Pick<Partial<Teaser>, "image"> & {
    size?: Size
  }

type PassedImageProps = Omit<ImageProps, "src">

type Props = BlogImageProps &
  PassedImageProps & {
    fallback?: ReactElement | null
  }

const WEEKLY_FUNDAMENTALS_IMAGE_COUNT = 8
const RESEARCH_IMAGE_COUNT = 4
const INTERVIEW_IMAGE_COUNT = 6

const projectLogoSize: {
  [key in Size]?: number
} = {
  xxl: 95,
  xl: 80,
  l: 45,
}

const projectRowColGap: { [key in Size]?: string } = {
  xxl: "gap-2 md:gap-4 lg:gap-6",
  xl: "gap-2 md:gap-4",
  l: "gap-2",
}

const headingSizeClasses: { [key in Size]?: string } = {
  xxl: "text-4xl md:text-5xl lg:text-6xl",
  xl: "text-3xl md:text-4xl lg:text-5xl",
  l: "text-2xl md:text-3xl lg:text-4xl",
  m: "text-xl md:text-2xl lg:text-3xl",
  s: "text-lg md:text-xl lg:text-2xl",
}

const generateImage = (
  { category, releaseNumber, image, size, projectMeta }: BlogImageProps,
  props: PassedImageProps
) => {
  if (image) {
    // Prefer manual image always
    return null
  }
  switch (category) {
    case "weekly-fundamentals": {
      const src = `/static/images/weekly-fundamentals/weekly-fundamentals-${
        ((releaseNumber ?? 1) % WEEKLY_FUNDAMENTALS_IMAGE_COUNT) + 1
      }.jpg`
      if (releaseNumber) {
        return (
          <Image
            src={src}
            {...props}
            className="text-primary flex items-center justify-center"
            alt={props.alt}
          >
            <p
              className={twMerge(
                "m-auto absolute text-neutral-50 font-medium",
                headingSizeClasses[size ?? "xl"]
              )}
              style={{ letterSpacing: ".5em" }}
            >
              #{releaseNumber}
            </p>
          </Image>
        )
      }
      return <Image src={src} {...props} alt={props.alt} />
    }
    case "crypto-research":
    case "interview": {
      // Generate image if manual one was not provided
      const max =
        category === "interview" ? INTERVIEW_IMAGE_COUNT : RESEARCH_IMAGE_COUNT
      return (
        <Image
          src={`/static/images/${category}/${
            category.split("-")[1] ?? category
          }-${Math.floor(Math.random() * max) || 1}.jpg`}
          className="relative"
          {...props}
          alt={props.alt}
        >
          {projectMeta && (
            <div
              className={twMerge(
                "m-auto absolute flex items-center justify-center left-0 right-0 text-left p-4",
                projectRowColGap[size ?? "l"]
              )}
            >
              <ProjectLogo
                logo={projectMeta.logo}
                name={projectMeta.name}
                size={projectLogoSize[size ?? "l"]}
                className="shrink"
              />
              <p
                className={twMerge(
                  "font-medium text-neutral-50",
                  headingSizeClasses[size ?? "l"]
                )}
              >
                {projectMeta.name}
              </p>
            </div>
          )}
        </Image>
      )
    }
  }
}

export const BlogImage = ({
  category,
  releaseNumber,
  image,
  fallback = null,
  size,
  projectMeta,
  ...imageProps
}: Props) => {
  const generatedImage = generateImage(
    { category, releaseNumber, image, size, projectMeta },
    imageProps
  )

  if (generatedImage) {
    return generatedImage
  } else if (!image) {
    return fallback
  }

  return <Image src={image} {...imageProps} />
}
